// ---------------------------------------------------------------------------
// usePersonas Hook (Unified)
// ---------------------------------------------------------------------------
// Full CRUD hook for personas. Replaces both the old usePersonas (funnel-only)
// and useUserFilters (discovery-only) hooks.
// Uses React Query for cache management across all components.
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  personaService,
  type Persona,
  type PersonaType,
  type CreatePersonaInput,
  type UpdatePersonaInput,
} from '@/services/v11/personaService'

// Re-export types for convenience
export type { Persona, PersonaType, CreatePersonaInput, UpdatePersonaInput }

const PERSONAS_KEY = ['personas'] as const

interface UsePersonasOptions {
  /** Filter by persona type: 'funnel' or 'flat' */
  readonly type?: PersonaType
  /** Whether to enable the query (default: true) */
  readonly enabled?: boolean
}

export function usePersonas(options: UsePersonasOptions = {}) {
  const { type, enabled = true } = options
  const queryClient = useQueryClient()

  const queryKey = type ? [...PERSONAS_KEY, { type }] : [...PERSONAS_KEY]

  // Query: list personas
  const query = useQuery({
    queryKey,
    queryFn: () => personaService.getAll(type),
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  })

  // Mutation: create
  const createMutation = useMutation({
    mutationFn: (input: CreatePersonaInput) => personaService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PERSONAS_KEY })
    },
  })

  // Mutation: update
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdatePersonaInput }) =>
      personaService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PERSONAS_KEY })
    },
  })

  // Mutation: delete
  const deleteMutation = useMutation({
    mutationFn: (id: number) => personaService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PERSONAS_KEY })
    },
  })

  // Mutation: set default
  const setDefaultMutation = useMutation({
    mutationFn: (id: number) => personaService.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PERSONAS_KEY })
    },
  })

  return {
    personas: (query.data ?? []) as readonly Persona[],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,

    createPersona: createMutation.mutateAsync,
    isCreating: createMutation.isPending,

    updatePersona: (id: number, input: UpdatePersonaInput) =>
      updateMutation.mutateAsync({ id, input }),
    isUpdating: updateMutation.isPending,

    deletePersona: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,

    setDefault: setDefaultMutation.mutateAsync,
    isSettingDefault: setDefaultMutation.isPending,
  }
}
